import { authorizedFetch } from './api';
import { countActiveParticipants } from './participantUtils';
import { SESSION_STATUS, SESSION_TYPE } from '../constants';

/**
 * Session teilen (Nov 2026): Lernende schicken ihre offene Session z. B. in die
 * WhatsApp-Klassengruppe und holen so Mitlernende in die App.
 *
 * Der Link führt auf die Detailseite: /session/<id>?via=teilen. Er umgeht keine
 * Hürde. Ohne Anmeldung geht es erst auf /login (LoginRedirect nimmt Pfad und
 * Query mit, utils/returnPath.js lässt /session/ zu), eine andere Schule bekommt
 * vom Backend 404. Der Parameter `via=teilen` bewirkt nur, dass die Seite den Knopf
 * „Platz sichern“ zeigt und den Beitritt als joinContext SHARE_LINK meldet
 * (Forschung FF 3, Codebuch § 3). Anwesenheit bestätigt weiter nur der Session-Code.
 *
 * Der Text nennt nur Thema, Datum und Uhrzeit, keine Namen.
 */

export const SHARE_VIA_PARAM = 'via';
export const SHARE_VIA_VALUE = 'teilen';
export const SHARE_JOIN_CONTEXT = 'SHARE_LINK';

// In der App ist window.location.origin https://localhost (Capacitor). Der Link
// muss aber im Browser der anderen funktionieren.
export const PUBLIC_WEB_ORIGIN = 'https://eachotter.de';

const TIME_ZONE = 'Europe/Berlin';

const SHAREABLE_STATUSES = [SESSION_STATUS.PLANNED, SESSION_STATUS.ACTIVE];

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Noch Platz frei (nur aktive Teilnahmen zählen, wie im Backend). */
export const hasFreeSeats = (session) => Boolean(session)
  && Number.isFinite(Number(session.maxParticipants))
  && countActiveParticipants(session.participants) < Number(session.maxParticipants);

/**
 * Darf diese Person diese Session teilen? Nur die eigene, offene Session (keine
 * Fokus-Session), geplant oder laufend, mit freiem Platz.
 */
export const canShareSession = (session, userId) => Boolean(session)
  && sameId(session.hostId, userId)
  && session.type === SESSION_TYPE.OPEN
  && !session.isFocusMode
  && SHAREABLE_STATUSES.includes(session.status)
  && hasFreeSeats(session);

/**
 * Darf jemand, der über einen geteilten Link kommt, hier einen Platz sichern?
 * Nicht die hostende Person, nicht wer schon dabei ist; LEFT darf zurück (der
 * erste joinContext bleibt dabei erhalten, SessionService).
 */
export const canJoinViaShareLink = (session, userId) => {
  if (!session || sameId(session.hostId, userId)) return false;
  if (session.type !== SESSION_TYPE.OPEN || session.isFocusMode) return false;
  if (!SHAREABLE_STATUSES.includes(session.status)) return false;
  const viewerStatus = session.viewerStatus ? String(session.viewerStatus).toUpperCase() : null;
  if (viewerStatus && viewerStatus !== 'LEFT') return false;
  return hasFreeSeats(session);
};

export const buildSessionShareUrl = (sessionId, { isNative = false } = {}) => {
  const origin = isNative || typeof window === 'undefined'
    ? PUBLIC_WEB_ORIGIN
    : window.location.origin;
  return `${origin}/session/${encodeURIComponent(sessionId)}?${SHARE_VIA_PARAM}=${SHARE_VIA_VALUE}`;
};

const formatDay = (date) => date.toLocaleDateString('de-DE', {
  weekday: 'long', day: 'numeric', month: 'long', timeZone: TIME_ZONE,
});

const formatTime = (date) => date.toLocaleTimeString('de-DE', {
  hour: '2-digit', minute: '2-digit', timeZone: TIME_ZONE,
});

/**
 * Satz ohne Link, z. B. „Lernst du mit? Bruchrechnen am Montag, 3. November um
 * 14:30 Uhr in EachOtter“. Läuft die Session schon: „… Bruchrechnen läuft gerade
 * in EachOtter“.
 */
export const buildSessionShareMessage = (session) => {
  const topic = String(session?.topic || '').trim() || 'Eine Session';
  if (session?.status === SESSION_STATUS.ACTIVE) {
    return `Lernst du mit? ${topic} läuft gerade in EachOtter`;
  }
  const start = session?.startTime ? new Date(session.startTime) : null;
  if (start && !Number.isNaN(start.getTime())) {
    return `Lernst du mit? ${topic} am ${formatDay(start)} um ${formatTime(start)} Uhr in EachOtter`;
  }
  return `Lernst du mit? ${topic} in EachOtter`;
};

/** Vollständiger Text mit Link (WhatsApp, Kopieren). */
export const buildSessionShareText = (session, url) => `${buildSessionShareMessage(session)}: ${url}`;

export const buildWhatsAppUrl = (text) => `https://wa.me/?text=${encodeURIComponent(text)}`;

export const SHARE_METHODS = ['native', 'whatsapp', 'copy'];

/**
 * Ereignis SESSION_SHARED (POST /api/v1/events, Codebuch § 3). Still: Ein
 * verlorenes Ereignis ist kein Grund für eine Meldung.
 */
export const trackSessionShared = (sessionId, method) => {
  if (!sessionId || !SHARE_METHODS.includes(method)) return Promise.resolve();
  try {
    return authorizedFetch('/api/v1/events', {
      method: 'POST',
      body: JSON.stringify({ events: [{ type: 'SESSION_SHARED', data: { sessionId, method } }] }),
      keepalive: true,
    }).then(() => undefined).catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
};
